<template>
  <div :class="rootClasses">
    <div v-if="$slots?.label || $slots?.helperText" class="mb-2">
      <h4
        v-if="$slots.label"
        class="font-semibold text-[var(--color-text-primary)]"
        :id="$attrs.id ? `${attrs.id}-label` : undefined"
      >
        <slot name="label" />
      </h4>
      <span v-if="$slots.helperText">
        <slot name="helperText" />
      </span>
    </div>
    <template v-if="loading">
      <Skeleton v-for="skeleton in 4" :key="skeleton" height="51px" class="mb-2" />
    </template>
    <draggable
      v-else
      id="reorder-list"
      v-model="value"
      v-bind="$attrs"
      tag="ul"
      class="pl-0 my-0"
      @start="drag = true"
      @end="onDragEnd()"
      :item-key="itemKey"
      :disabled="disabled || !sortable"
      :aria-labelledby="$attrs.id ? `${attrs.id}-label` : undefined"
    >
      <template #item="{ element }">
        <li
          class="drag-item flex justify-start items-center px-4 py-1 cursor-pointer w-full"
          :class="{
            'drag-item-highlight': isSelected(element),
            'drag-item-disabled': disabled,
            'drag-item-deleted': isElementDeleted(element),
          }"
          @pointerdown="select(element)"
          @pointerup="selected = null"
          :draggable="sortable && !isElementDeleted(element)"
          :aria-label="`${getOptionLabel(element)}`"
        >
          <i
            class="drag-button material-symbols-rounded p-datatable-reorderablerow-handle mr-4"
            aria-hidden="true"
            v-if="sortable"
            >drag_indicator</i
          >
          <span class="text-sm grow">{{ getOptionLabel(element) }}</span>

          <slot name="custom-action" :element="element" />

          <div class="ml-auto">
            <IconButton
              v-if="!isElementDeleted(element)"
              class="remove-button c-icon-button"
              :class="{ 'remove-button--visible': removeable && isElementRemovable(element) }"
              @click="removeItem(element)"
              :aria-label="`Remove ${getOptionLabel(element)}`"
            >
              <template #icon>
                <i class="material-symbols-rounded" aria-hidden="true"> close </i>
              </template>
            </IconButton>
            <IconButton
              v-if="isElementDeleted(element) && restorable"
              class="restore-button c-icon-button"
              :class="{ 'restore-button--visible': restorable }"
              @click="restoreItem(element)"
              :aria-label="`Restore ${getOptionLabel(element)}`"
            >
              <template #icon>
                <i class="material-symbols-rounded" aria-hidden="true"> add_circle_outline </i>
              </template>
            </IconButton>
          </div>
        </li>
      </template>
    </draggable>
  </div>
</template>
<script lang="ts" setup>
import { ref, useAttrs, type PropType } from 'vue';
import draggable from 'vuedraggable';
import { IconButton } from '../../buttons/icon-button';
import { Skeleton } from '../../misc/skeleton';
import { useInputState } from '../../controls';
import { isEqual } from 'lodash-es';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  loading: {
    type: Boolean,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  itemKey: {
    type: String,
    default: 'id',
  },
  optionLabel: {
    type: String,
    default: 'name',
  },
  removeable: {
    type: Boolean,
    default: false,
  },
  isElementRemovable: {
    type: Function,
    default: (_value: any) => true,
  },
  isElementDeleted: {
    type: Function,
    default: (_value: any) => false,
  },
  restorable: {
    type: Boolean,
    default: false,
  },
  shouldSoftDeleteElement: {
    type: Function,
    default: (_value: any) => false,
  },
  sortable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['removeItem', 'restoreItem', 'select', 'onDragEnd']);

const value = defineModel({ type: Array as PropType<any[]>, default: (): any[] => [] });

const drag = ref(false);

const selected = ref();
const select = (element: any) => {
  if (!props.sortable) {
    return;
  }
  selected.value = element;
  emit('select');
};

const isSelected = (element: any): boolean => {
  if (selected?.value && isEqual(element, selected.value)) {
    return true;
  }
  return false;
};

const onDragEnd = async () => {
  drag.value = false;
  selected.value = null;
  emit('onDragEnd');
};
const removeItem = (element: any) => {
  if (props.restorable && props.shouldSoftDeleteElement(element)) {
    // Soft delete: set deleted_at instead of removing
    value.value = value.value.map((item: any) => {
      if (item?.[props.itemKey] === element?.[props.itemKey]) {
        return {
          ...item,
          deleted_at: new Date(),
        };
      }
      return item;
    });
  } else {
    // Hard delete: remove from array
    value.value = value.value?.filter((item) => {
      return item?.[props.itemKey] !== element?.[props.itemKey];
    });
  }
  emit('removeItem', element);
};

const restoreItem = (element: any) => {
  // Restore: remove deleted_at field
  value.value = value.value.map((item: any) => {
    if (item?.[props.itemKey] === element?.[props.itemKey]) {
      return {
        ...item,
        deleted_at: null,
      };
    }
    return item;
  });
  emit('restoreItem', element);
};

function getOptionLabel(elementObject: any): string | undefined {
  const segments = props.optionLabel.split('.');
  let current: any = elementObject;
  for (const segment of segments) {
    if (!(segment in current)) return undefined;
    current = current[segment];
  }
  return current;
}

const attrs = useAttrs();

const { classes: rootClasses } = useInputState(attrs);
</script>
<style scoped lang="scss">
:deep(#reorder-list) {
  list-style: none;
  margin: 0;
  padding: 0;
}

.drag-item {
  background: $shade000;
  border: solid 1px $shade400;
  border-bottom: none;
  border-radius: 0;
  color: #000000;
  margin: 0;

  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-child {
    border-bottom: solid 1px $shade400;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  &:only-child {
    border-radius: 8px;
    border-bottom: solid 1px $shade400;
  }

  &:hover:not(.drag-item-disabled):not(.drag-item-deleted) {
    background: var(--color-surface-hover); // #E3E6EB
  }

  .remove-button {
    color: var(--neutral-500); // #5C6A7E
    opacity: 0;
    pointer-events: none;
    &.remove-button--visible {
      opacity: 1;
      pointer-events: auto;
    }
  }
  &.drag-item-highlight {
    background: var(--color-surface-hover);
  }
  &.drag-item-disabled {
    pointer-events: none;
  }
  &.drag-item-deleted {
    background-color: $shade200 !important;
    color: $shade600 !important;
    opacity: 0.7;
  }
  .drag-button {
    color: $shade600;
  }
  .restore-button {
    color: #6c757d;
    opacity: 0;
    pointer-events: none;
    &.restore-button--visible {
      opacity: 1;
      pointer-events: auto;
    }
  }
  :focus {
    background: #c5dbff;
  }

  &.sortable-chosen,
  &.sortable-drag {
    box-shadow: 0 2px 8px 0 rgba(24, 27, 33, 0.08);
    border-radius: 8px;
    border: solid 1px $shade400;
    background: $shade000;
  }
}
</style>

<style lang="scss">
/* Drag ghost may be cloned outside the scoped tree */
.drag-item.sortable-drag {
  box-shadow: 0 2px 8px 0 rgba(24, 27, 33, 0.08);
  border-radius: 8px;
  border: solid 1px var(--neutral-200, #cad0d8);
}
</style>
